import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import OnboardingGradient from "@/assets/OnboardingGradient.svg";
import OnboardingText from "@/assets/OnboardingText.svg";
import OnboardingVector from "@/assets/OnboardingVector.svg";
import OnboardingToolbar from "@/assets/OnboardingToolbar.png";
import OnboardingSkeleton from "@/assets/OnboardingSkeleton.svg";
import ChevronLeftSmall from "@/assets/icons/ChevronLeftSmall.svg";
import ChevronRightSmall from "@/assets/icons/ChevronRightSmall.svg";
import Checkmark20 from "@/assets/icons/Checkmark20.svg";

const Onboarding = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [mediaModalOpened, setMediaModalOpened] = useState(false);
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "TOOLBAR_MEDIA_MODAL_OPENED") {
        setMediaModalOpened(true);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const roles = [
    {
      id: "qa",
      title: "QA",
      description: "Create detailed bug reports",
    },
    {
      id: "pm",
      title: "PM",
      description: "Describe tasks and workflows",
    },
    {
      id: "dev",
      title: "Dev",
      description: "Fix bugs faster with context",
    },
    {
      id: "other",
      title: "Other",
      description: "I simply want a screen capture tool",
    },
  ];

  const featuresByRole = {
    qa: [
      {
        title: "Capture and share bugs",
        description: "With all the technical data developers need",
      },
      {
        title: "Instant Replay",
        description: "Capture the last 30 seconds without missing a moment",
      },
      {
        title: "Get reproduction steps",
        description: "Automatically generated reproduction steps",
      },
      {
        title: "AI-Powered Summaries",
        description: "Summarize videos and organize report data",
      },
    ],
    pm: [
      {
        title: "Record workflows & feedback",
        description: "Share workflows & feedback with your team.",
      },
      {
        title: "Workspaces for collaboration",
        description: "Organize projects and track progress.",
      },
      {
        title: "AI-Powered Summaries",
        description: "Making communication clearer and faster.",
      },
      {
        title: "Integrations",
        description: "Send tasks directly to your task manager.",
      },
    ],
    dev: [
      {
        title: "Fix bugs with AI",
        description: "Using our Claude Code MCP",
      },
      {
        title: "Detailed bug reports",
        description: "With all technical data needed",
      },
      {
        title: "Instant Replay",
        description: "Replay the exact flow leading up to an issue",
      },
      {
        title: "AI-Powered Summaries",
        description: "Making communication clearer and faster",
      },
    ],
    other: [
      {
        title: "Screen Recordings",
        description: "Capture screen recordings effortlessly",
      },
      {
        title: "Instant Replay",
        description: "Capture the last 30 seconds without missing a moment",
      },
      {
        title: "Quick Screenshots",
        description: "With annotations and drawing tools",
      },
      {
        title: "Shareable Links",
        description: "Instantly share what you captured with anyone",
      },
    ],
  };

  const features = selectedRole
    ? featuresByRole[selectedRole as keyof typeof featuresByRole]
    : featuresByRole.qa;

  const steps = [
    {
      title: "Welcome to Toolbar",
      description:
        "The fastest way to report bugs and share feedback with your team. Think Loom, but built for Dev Teams",
      image: OnboardingToolbar,
    },
    {
      title: "What's your role?",
      description: "We'll customize your experience based on your role",
      type: "role-selection",
    },
    {
      title: "Turn Recordings into Tasks",
      description:
        "Record workflows and let AI automatically convert them into actionable tasks for your team.",
      type: "features",
    },
  ];

  const handleNext = () => {
    setDirection(1);
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      activateToolbar();
      setIsComplete(true);
    }
  };

  const handleBack = () => {
    setDirection(-1);
    setCurrentStep(currentStep - 1);
  };

  const activateToolbar = async () => {
    setIsActivating(true);

    try {
      // Check if chrome.runtime is available
      if (typeof chrome === "undefined" || !chrome.runtime) {
        console.error("Chrome runtime not available");
        return;
      }

      const response = await chrome.runtime.sendMessage(
        "jjikojjfkjcbjklhldnjdnelfmjjmifg",
        { action: "activateFromWebsite" },
      );

      // console.log("Response:", response);

      if (response?.success) {
        // console.log("Toolbar activated:", response.action);
      } else {
        console.error("Activation failed:", response);
      }
    } catch (error) {
      console.error("Error activating Toolbar:", error);
    }
  };

  return (
    <div className="fixed bg-black/[0.56] inset-0 flex items-center justify-center overflow-hidden">
      {!isActivating && (
        <img
          src={OnboardingGradient}
          alt="Background Gradient"
          className="absolute top-1/2 max-w-[1400px] left-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      )}
      <div className="relative z-10 flex flex-col items-center justify-center max-w-[800px] px-8">
        {isComplete ? (
          mediaModalOpened ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="flex max-w-[320px] flex-col items-center text-center"
            >
              <h1 className="text-[28px] leading-[36px] font-medium text-white mb-3">
                You're all set, now go kill those bugs!
              </h1>
              <a
                href="https://x.com/usetoolbar"
                target="_blank"
                className="font-medium text-[14px] leading-[20px] underline text-white/[0.32]"
              >
                and follow us on X!
              </a>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="fixed inset-0 flex items-center justify-center"
            >
              <img
                src={OnboardingSkeleton}
                alt="Onboarding Complete"
                className="w-full h-full object-contain"
              />
            </motion.div>
          )
        ) : (
          <>
            <AnimatePresence mode="wait" initial={false} custom={direction}>
              <motion.div
                key={currentStep}
                custom={direction}
                variants={{
                  enter: (direction: number) => ({
                    x: direction > 0 ? 300 : -300,
                    opacity: 0,
                  }),
                  center: {
                    x: 0,
                    opacity: 1,
                  },
                  exit: (direction: number) => ({
                    x: direction > 0 ? -300 : 300,
                    opacity: 0,
                  }),
                }}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: 0.25,
                  ease: [0.34, 1.35, 0.64, 1],
                }}
                className="flex flex-col items-center"
              >
                {/* Title */}
                <h1 className="text-[28px] leading-[36px] font-medium text-white mb-3 text-center">
                  {steps[currentStep].title}
                </h1>

                {/* Description */}
                <p className="font-medium text-[14px] leading-[20px] text-white/80 mb-[32px] text-center max-w-[372px]">
                  {steps[currentStep].description}
                </p>

                {/* Content based on step type */}
                {steps[currentStep].type === "role-selection" ? (
                  <>
                    {/* Role Selection Buttons */}
                    <div className="flex flex-col gap-3 mb-[32px] w-full max-w-[720px]">
                      {roles.map((role) => (
                        <button
                          key={role.id}
                          onClick={() => {
                            setSelectedRole(role.id);
                            handleNext();
                          }}
                          style={{ outline: "none" }}
                          className={`rounded-[24px] flex flex-col items-center justify-center border-[2px] transition-all w-[400px] h-[84px] text-center focus:outline-none focus:ring-0 focus:border-opacity-0 outline-none ${
                            selectedRole === role.id
                              ? "bg-white/[0.16] border-white/[0.24]"
                              : "bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.08]"
                          }`}
                        >
                          <h3 className="text-[14px] leading-[20px] font-medium text-white mb-1">
                            {role.title}
                          </h3>
                          <p className="text-[14px] leading-[20px] text-white/[0.64] font-medium">
                            {role.description}
                          </p>
                        </button>
                      ))}
                    </div>

                    {/* Back Button */}
                    <button
                      onClick={handleBack}
                      style={{
                        boxShadow: "0px 4px 48px 0px rgba(0, 0, 0, 0.06)",
                      }}
                      className="bg-white/[0.08] rounded-[12px] h-[36px] w-[200px] shrink-0 text-white text-[14px] leading-[20px] font-medium hover:bg-white/[0.16] transition-all flex items-center gap-[6px] justify-center"
                    >
                      <img src={ChevronRightSmall} />
                      Back
                    </button>
                  </>
                ) : steps[currentStep].type === "features" ? (
                  <>
                    {/* Features List */}
                    <div className="flex flex-col gap-1 mb-[32px] w-full">
                      {features.map((feature, index) => (
                        <div
                          key={index}
                          className="rounded-[14px] py-[11px] px-3 text-[14px] leading-[20px] odd:bg-white/[0.08] flex items-center gap-2 w-[400px]"
                        >
                          <img src={Checkmark20} />
                          {/* <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 8L6.5 11.5L13 5"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div> */}
                          <div className="text-left">
                            <span className=" font-medium text-white">
                              {feature.title}
                            </span>
                            <span className=" text-white/56 ml-1">
                              - {feature.description}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Finish Button */}
                    <div className="flex gap-2 max-w-[200px] w-full">
                      <button
                        onClick={handleBack}
                        style={{
                          boxShadow: "0px 4px 48px 0px rgba(0, 0, 0, 0.06)",
                        }}
                        className="bg-white/[0.08] rounded-[12px] h-[36px] w-[52px] shrink-0 text-gray-50 text-[14px] leading-[20px] font-medium hover:bg-white/[0.16] transition-all flex items-center gap-[6px] justify-center"
                      >
                        <img src={ChevronRightSmall} />
                      </button>
                      <button
                        onClick={handleNext}
                        className="bg-white w-full h-[36px] text-gray-50 px-8 rounded-[12px] text-[14px] leading-[20px] font-medium hover:bg-white/80 transition-all flex items-center gap-[6px] justify-center"
                      >
                        Finish
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Image Container with handwritten note */}
                    <div className="relative mb-[32px]">
                      {/* Background vector */}
                      <img
                        src={OnboardingVector}
                        alt="Background"
                        className="w-[400px] h-auto rounded-[24px] border-[2px] border-white/[0.08]"
                      />

                      {/* Toolbar image overlaid on the vector */}
                      <div className="absolute top-[106px] left-1/2 -translate-x-1/2 w-[314px]">
                        <img
                          src={steps[currentStep].image}
                          alt="Onboarding"
                          className="w-full h-full object-contain"
                        />
                      </div>

                      {/* Handwritten note */}
                      <div className="absolute top-[58px] right-[-91px]">
                        <img
                          src={OnboardingText}
                          alt="Note"
                          className="w-[186px]"
                        />
                      </div>
                    </div>

                    {/* Next Button */}
                    <button
                      onClick={handleNext}
                      className="bg-white h-[36px] text-gray-50 px-8 rounded-[12px] text-[14px] leading-[20px] font-medium hover:bg-white/80 transition-all flex items-center gap-[6px] min-w-[200px] justify-center"
                    >
                      Next
                      <img src={ChevronLeftSmall} />
                    </button>
                  </>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Progress Indicators */}
            <div className="flex gap-1 justify-center mt-5">
              {steps.map((_, index) => (
                <motion.div
                  key={index}
                  className={`h-[8px] rounded-full transition-all ${
                    index === currentStep
                      ? "w-[24px] bg-white"
                      : "w-[8px] bg-white/[0.32]"
                  }`}
                  layout
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
